import {useQuery} from '@tanstack/react-query'
import {useMemo, useState} from 'react'
import {Badge} from '@/components/ui/badge'
import {Clock, CheckCircle, Ban} from 'lucide-react'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {ErrorBanner} from '@/components/ui/error-banner'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {AccessRequestControllerMyRequestsEndpoint, FunctionGroupRequestControllerMyRequestsEndpoint} from '@/types/endpoint-contracts'
import {RequestStatus} from '@/types/request-base'
import {formatDateOnly} from '@/lib/date-utils'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {MutedText} from '@/components/shared/MutedText'
import {TruncatedCell} from '@/components/shared/TruncatedCell'
import {NewRequestButtons} from '@/features/my/NewRequestButtons'
import {PageLayout, TabPanelContent} from '@/components/shared/PageLayout'
import type {TabDefinition} from '@/components/shared/PageLayout'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {SortableHeader} from '@/components/ui/sortable-header'
import {CONCEPT_ICON} from '@/lib/concepts'

function AccessRequestsTab() {
    const services = useServices()
    const {user} = useAuth()
    const i18n = useI18n()

    const statusBadge = (status: RequestStatus) => {
        switch (status) {
            case RequestStatus.SUBMITTED:
                return <Badge variant="outline" className="gap-1"><Clock
                    className="h-3 w-3"/>{i18n.myRequests.statusSubmitted}</Badge>
            case RequestStatus.IN_PROGRESS:
                return <Badge variant="secondary" className="gap-1"><Clock
                    className="h-3 w-3"/>{i18n.myRequests.statusInProgress}</Badge>
            case RequestStatus.COMPLETED:
                return <Badge variant="success" className="gap-1"><CheckCircle
                    className="h-3 w-3"/>{i18n.myRequests.statusCompleted}</Badge>
            case RequestStatus.CANCELLED:
                return <Badge variant="destructive" className="gap-1"><Ban
                    className="h-3 w-3"/>{i18n.myRequests.statusCancelled}</Badge>
        }
    }

    const {
        page, pageSize, queryString,
        sortBy, sortDir, setSort,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters,
        setSearchAndFilters,
    } = useServerPagination({
        endpoint: AccessRequestControllerMyRequestsEndpoint,
        sortable: true,
        defaultSortBy: AccessRequestControllerMyRequestsEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const hasActiveFiltersAr = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
    }, [searchAndFilters])

    const {data, isLoading, error} = useQuery({
        queryKey: ['my-requests', queryString],
        queryFn: () => services.accessRequests.myRequests(Object.fromEntries(new URLSearchParams(queryString))),
        enabled: !!user,
    })

    return (
        <TabPanelContent
            filters={
                <UnifiedSearchBar
                    contract={AccessRequestControllerMyRequestsEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.myRequests.searchPlaceholder as string}
                    testIdNamespace="my-requests"
                />
            }
            pagination={data && data.totalElements > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={data.totalElements}
                    totalPages={data.totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <ErrorBanner error={error} className="mb-4"/>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-beneficiary">
                                    <SortableHeader
                                        label={i18n.accessRequests.beneficiary as string}
                                        sortKey={AccessRequestControllerMyRequestsEndpoint.sort.BENEFICIARY_IDENTITY_PARTY_DISPLAY_NAME}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                                <TableHead data-testid="col-justification">{i18n.accessRequests.justification}</TableHead>
                                <TableHead data-testid="col-items">{i18n.accessRequests.items}</TableHead>
                                <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                                <TableHead data-testid="col-created-at">
                                    <SortableHeader
                                        label={i18n.common.createdAt as string}
                                        sortKey={AccessRequestControllerMyRequestsEndpoint.sort.CREATED_AT}
                                        currentSortBy={sortBy}
                                        currentSortDir={sortDir}
                                        onSort={setSort}
                                    />
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading && (
                                <TableEmptyState
                                    colSpan={5}
                                    hasFilters={false}
                                    matchMessage={i18n.common.loading}
                                    emptyMessage={i18n.common.loading}
                                />
                            )}
                            {data && data.content.length === 0 && (
                                <TableEmptyState
                                    colSpan={5}
                                    hasFilters={hasActiveFiltersAr}
                                    matchMessage={i18n.myRequests.noRequestsMatch}
                                    emptyMessage={i18n.myRequests.noAccessRequestsFound}
                                />
                            )}
                            {data?.content.map(request => (
                                <ClickableTableRow key={request.id}
                                                   to={routes.my.requests.detail(request.id)}>
                                    <TableCell>
                                        <TextLink to={routes.my.requests.detail(request.id)}
                                                    variant="bold"
                                                    stopPropagation
                                                    testId={`link-request-${request.id}`}>
                                            {request.beneficiary.displayName}
                                        </TextLink>
                                    </TableCell>
                                    <TruncatedCell>
                                        {request.justification}
                                    </TruncatedCell>
                                    <TableCell>{request.items.length}</TableCell>
                                    <TableCell>{statusBadge(request.status)}</TableCell>
                                    <TableCell>
                                        <MutedText size="sm">{formatDateOnly(request.createdAt)}</MutedText>
                                    </TableCell>
                                </ClickableTableRow>
                            ))}
                        </TableBody>
                    </Table>
        </TabPanelContent>
    )
}

function FunctionGroupRequestsTab() {
    const services = useServices()
    const {user} = useAuth()
    const i18n = useI18n()

    const statusBadge = (status: RequestStatus) => {
        switch (status) {
            case RequestStatus.SUBMITTED:
                return <Badge variant="outline" className="gap-1"><Clock
                    className="h-3 w-3"/>{i18n.functionGroupRequests.statusSubmitted}</Badge>
            case RequestStatus.IN_PROGRESS:
                return <Badge variant="secondary" className="gap-1"><Clock
                    className="h-3 w-3"/>{i18n.functionGroupRequests.statusInProgress}</Badge>
            case RequestStatus.COMPLETED:
                return <Badge variant="success" className="gap-1"><CheckCircle
                    className="h-3 w-3"/>{i18n.functionGroupRequests.statusCompleted}</Badge>
            case RequestStatus.CANCELLED:
                return <Badge variant="destructive" className="gap-1"><Ban
                    className="h-3 w-3"/>{i18n.functionGroupRequests.statusCancelled}</Badge>
        }
    }

    const {
        page, pageSize, queryString,
        sortBy, sortDir, setSort,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters,
        setSearchAndFilters,
    } = useServerPagination({
        endpoint: FunctionGroupRequestControllerMyRequestsEndpoint,
        sortable: true,
        defaultSortBy: FunctionGroupRequestControllerMyRequestsEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const hasActiveFiltersFg = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
    }, [searchAndFilters])

    const {data, isLoading, error} = useQuery({
        queryKey: ['my-fg-requests', queryString],
        queryFn: () => services.functionGroupRequests.myRequests(Object.fromEntries(new URLSearchParams(queryString))),
        enabled: !!user,
    })

    return (
        <TabPanelContent
            filters={
                <UnifiedSearchBar
                    contract={FunctionGroupRequestControllerMyRequestsEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.myRequests.searchPlaceholder as string}
                    testIdNamespace="my-fg-requests"
                />
            }
            pagination={data && data.totalElements > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={data.totalElements}
                    totalPages={data.totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <ErrorBanner error={error} className="mb-4"/>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-fg-beneficiary">
                            <SortableHeader
                                label={i18n.functionGroupRequests.beneficiary as string}
                                sortKey={FunctionGroupRequestControllerMyRequestsEndpoint.sort.BENEFICIARY_IDENTITY_PARTY_DISPLAY_NAME}
                                currentSortBy={sortBy}
                                currentSortDir={sortDir}
                                onSort={setSort}
                            />
                        </TableHead>
                        <TableHead data-testid="col-fg-justification">{i18n.functionGroupRequests.justification}</TableHead>
                        <TableHead data-testid="col-fg-items">{i18n.functionGroupRequests.items}</TableHead>
                        <TableHead data-testid="col-fg-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-fg-created-at">
                            <SortableHeader
                                label={i18n.common.createdAt as string}
                                sortKey={FunctionGroupRequestControllerMyRequestsEndpoint.sort.CREATED_AT}
                                currentSortBy={sortBy}
                                currentSortDir={sortDir}
                                onSort={setSort}
                            />
                        </TableHead>
                    </TableRow>
                </TableHeader>
                        <TableBody>
                            {isLoading && (
                                <TableEmptyState
                                    colSpan={5}
                                    hasFilters={false}
                                    matchMessage={i18n.common.loading}
                                    emptyMessage={i18n.common.loading}
                                />
                            )}
                            {data && data.content.length === 0 && (
                                <TableEmptyState
                                    colSpan={5}
                                    hasFilters={hasActiveFiltersFg}
                                    matchMessage={i18n.myRequests.noRequestsMatch}
                                    emptyMessage={i18n.functionGroupRequests.noFunctionGroupRequestsFound}
                                />
                            )}
                            {data?.content.map(request => (
                                <ClickableTableRow key={request.id}
                                                   to={routes.my.functionGroupRequests.detail(request.id)}>
                                    <TableCell>
                                        <TextLink to={routes.my.functionGroupRequests.detail(request.id)}
                                                    variant="bold"
                                                    stopPropagation
                                                    testId={`link-fg-request-${request.id}`}>
                                            {request.beneficiary.displayName}
                                        </TextLink>
                                    </TableCell>
                                    <TruncatedCell>
                                        {request.justification}
                                    </TruncatedCell>
                                    <TableCell>{request.items.length}</TableCell>
                                    <TableCell>{statusBadge(request.status)}</TableCell>
                                    <TableCell>
                                        <MutedText size="sm">{formatDateOnly(request.createdAt)}</MutedText>
                                    </TableCell>
                                </ClickableTableRow>
                            ))}
                        </TableBody>
                    </Table>
        </TabPanelContent>
    )
}

export function MyRequests() {
    const i18n = useI18n()
    const services = useServices()
    const {user} = useAuth()
    const [activeTab, setActiveTab] = useState('access-requests')

    const {data: accessCountData} = useQuery({
        queryKey: ['my-requests-count'],
        queryFn: () => services.accessRequests.myRequests({size: 1}),
        enabled: !!user,
    })
    const {data: fgCountData} = useQuery({
        queryKey: ['my-fg-requests-count'],
        queryFn: () => services.functionGroupRequests.myRequests({size: 1}),
        enabled: !!user,
    })
    const accessCount = accessCountData?.totalElements
    const fgCount = fgCountData?.totalElements

    const tabs: TabDefinition[] = [
        {
            key: 'access-requests',
            label: String(i18n.myRequests.tabAccessRequests),
            count: accessCount && accessCount > 0 ? accessCount : undefined,
        },
        {
            key: 'fg-requests',
            label: String(i18n.myRequests.tabFunctionGroupRequests),
            count: fgCount && fgCount > 0 ? fgCount : undefined,
        },
    ]

    const activeTabLabel = tabs.find(t => t.key === activeTab)?.label ?? ''
    /* WHY: the H2 shows just the active tab label; the section ("My Requests")
       is conveyed by the sidebar selection and the route. Concatenating both
       reads as "My Requests: Access Requests", which made the layout title
       diverge from the screencast/test contract that asserts only the tab. */
    const pageTitle = activeTabLabel || String(i18n.myRequests.title)
    const tabDescriptions: Record<string, string> = {
        'access-requests': String(i18n.myRequests.accessRequestsDescription),
        'fg-requests': String(i18n.myRequests.functionGroupRequestsDescription),
    }
    const pageDescription = tabDescriptions[activeTab] ?? String(i18n.myRequests.subtitle)

    return (
        <PageLayout
            data-testid="my-requests-page"
            variant="tabbed-list"
            icon={CONCEPT_ICON.myRequest}
            title={pageTitle}
            description={pageDescription}
            actions={<NewRequestButtons/>}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
        >
            {activeTab === 'access-requests' && <AccessRequestsTab/>}
            {activeTab === 'fg-requests' && <FunctionGroupRequestsTab/>}
        </PageLayout>
    )
}
